import styled from "@emotion/styled";
import { Box, Container, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { getAnalytics, logEvent } from "firebase/analytics";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageSectionBG from "../PageSectionBG";
import ScrollNavTab from "../ScrollNavTab";
import CapabilitiesFloatingSection from "./CapabilitiesFloatingSection";

interface CapabilitiesSectionProps {}

interface Section {
  title: string;
  linkPath: string;
  description: string;
}

const sections: Section[] = [
  {
    title: "GenAI DEVELOPMENT",
    linkPath: "/capabilities/gen-ai-development",
    description:
      "Empower your business with cutting-edge Generative AI solutions that drive innovation and efficiency.",
  },
  {
    title: "PRODUCT DESIGN",
    linkPath: "/capabilities/product-design",
    description:
      "Surpass competitors with captivating products, services, and user experiences.",
  },
  {
    title: "INNOVATIVE TECH",
    linkPath: "/capabilities/innovative-technology",
    description:
      "Drive innovation and efficiency with edge technology solutions.",
  },
  {
    title: "SOFTWARE DEVELOPMENT",
    linkPath: "/capabilities/software-development",
    description:
      "Elevate your digital presence with robust, scalable, and innovative solutions.",
  },
  {
    title: "BUSINESS & TECH",
    linkPath: "/capabilities/",
    description:
      "Optimize and transform your business with strategic and advanced tech solutions.",
  },
];

const CapabilitiesSection: React.FC<CapabilitiesSectionProps> = () => {
  const introsectionheight = 0; //In VH. This is the amount of space above the first section in the runway. When above it there will be no active section.
  const outrosectionheight = 20; //In VH. This is the amount of space below the last section in the runway. When below it there will be no active section.
  const contentsectionheight = 100; //In VH. This is the height of each section in the runway.

  const analytics = getAnalytics();

  const [activeSection, setActiveSection] = useState<number>(-1);
  // const [activeSectionScrolledPercent, setActiveSectionScrolledPercent] = useState<number>(0);
  const [pauseCheckForSectionInView, setPauseCheckForSectionInView] =
    useState<boolean>(false);

  // Initialize refs to include boxes before and after sections
  const refs = useMemo(
    () =>
      Array(sections.length + 2)
        .fill(0)
        .map(() => React.createRef<HTMLDivElement>()),
    []
  );

  const theme = useTheme();

  const handleScroll = useCallback(() => {
    if (!pauseCheckForSectionInView) {
      refs.forEach((sectionRef, index) => {
        const sectionBoundingRect = sectionRef.current?.getBoundingClientRect();

        if (
          sectionBoundingRect?.top! <= window.innerHeight / 2 &&
          sectionBoundingRect?.bottom! >= window.innerHeight / 2
        ) {
          // const sectionHeight = sectionBoundingRect?.height!;
          // const sectionTop = sectionBoundingRect?.top!;
          // const sectionBottom = sectionBoundingRect?.bottom!;
          // const sectionScrollCompletion = (window.innerHeight / 2 - sectionTop) / sectionHeight;

          // setActiveSectionScrolledPercent(sectionScrollCompletion);
          setActiveSection(index - 1); // Adjust for extra ref at the beginning
        }
      });
    }
  }, [pauseCheckForSectionInView, refs]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScrollEnd = () => {
      setPauseCheckForSectionInView(false);
    };

    const debouncedScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScrollEnd, 150);
      handleScroll();
    };

    window.addEventListener("scroll", debouncedScroll);

    return () => {
      window.removeEventListener("scroll", debouncedScroll);
      clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);

  const handleScrollNavTabClick = (index: number) => {
    console.log(refs);
    setPauseCheckForSectionInView(true);
    setActiveSection(index);
    refs[index + 1]?.current?.scrollIntoView({ behavior: "smooth" }); // Adjust for extra ref at the beginning

    logEvent(analytics, "home_capabilities_section_clicked", {
      section: sections[index].title,
    });
  };

  return (
    <PageSectionBG
      bgcolor={theme.palette.common.white}
      paddingTop={0}
      paddingBottom={12}
      position={"relative"}
      id="capabilities-section"
    >
      <Container maxWidth="xl">
        <Grid container position="relative" width={"100%"}>
          <Grid xs={12} sm={12} md={12} lg={7} position="relative">
            <Stack
              gap={10}
              position="sticky"
              paddingTop={10}
              top={theme.spacing(10)}
            >
              <Stack gap={3}>
                <Typography
                  color={theme.palette.common.black}
                  className="noselect"
                  variant="textXLBold"
                >
                  Our Capabilities
                </Typography>
                <Typography variant="textXLLight" maxWidth="528px">
                  We deliver end-to-end digital solutions that drive business
                  growth and enhance user engagement.
                </Typography>
              </Stack>
              <Stack>
                {sections.map((section, index) => (
                  <ScrollNavTab
                    key={index}
                    variant="xl3"
                    title={section.title}
                    isActive={activeSection === index}
                    handleClick={() => handleScrollNavTabClick(index)}
                    backgroundcolor={theme.palette.gradients.wildGreenBoy}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>

          <FullLengthContentGrid
            xs={12}
            sm={12}
            md={12}
            lg={5}
            position="relative"
            introsectionheight={introsectionheight}
            outrosectionheight={outrosectionheight}
            contentsectionheight={contentsectionheight}
            theme={theme}
          >
            <FullLengthContent theme={theme}>
              {
                //IF breakpoint is greater than md than show the first Box
              }

              <Box
                component="div"
                className="intro-spacer"
                height={`${introsectionheight}vh`}
                ref={refs[0]}
              />
              {sections.map((section, index) => (
                <CapabilitiesFloatingSection
                  key={index}
                  direction={index % 2}
                  title={section.title}
                  linkPath={section.linkPath}
                  description={section.description}
                  height={contentsectionheight}
                  ref={refs[index + 1]} // Adjust for extra ref at the beginning
                />
              ))}
              <Box
                component="div"
                className="outro-spacer"
                height={`${outrosectionheight}vh`}
                ref={refs[sections.length + 1]}
              />
            </FullLengthContent>
          </FullLengthContentGrid>
        </Grid>
      </Container>

      <Box
        component="div"
        position="absolute"
        top="0px"
        right="0px"
        height="100%"
      >
        <Box
          component="div"
          width="4.38rem"
          height="100vh"
          position="sticky"
          top="0px"
          sx={{
            background: theme.palette.gradients.wildGreenBoy,
            backgroundImage:
              'url("../assets/capabilities/capability_texture.svg")',
            backgroundSize: "cover",
          }}
        />
      </Box>
    </PageSectionBG>
  );
};

export default CapabilitiesSection;

interface FullLengthContentGridProps {
  theme: any;
  introsectionheight: number;
  outrosectionheight: number;
  contentsectionheight: number;
}

const FullLengthContentGrid = styled(Grid)<FullLengthContentGridProps>`
  ${({ theme }) => theme.breakpoints.down("lg")} {
    height: ${({ contentsectionheight }) => {
      return `${contentsectionheight * sections.length}vh`;
    }};
    .intro-spacer {
      display: none;
    }
    .outro-spacer {
      display: none;
    }
  }
  ${({ theme }) => theme.breakpoints.up("lg")} {
    border-left: 1px solid #C8C8C8;
    justify-content: start;
    padding-left: 1.5rem;
    display: flex;
    height: ${({
      introsectionheight,
      outrosectionheight,
      contentsectionheight,
    }) => {
      return `${
        introsectionheight +
        outrosectionheight +
        contentsectionheight * sections.length
      }vh`;
    }};
`;

interface FullLengthContentProps {
  theme: any;
}

const FullLengthContent = styled.div<FullLengthContentProps>`
  position: absolute;
  overflow: visible;
  z-index: 10;
  top: 0;
`;

/* interface FixedContentSectionProps {
  theme: any;
} */

/* const FixedContentSection = styled.div<FixedContentSectionProps>`
  position: sticky;
  top: ${({ theme }) => theme.spacing(8)};

  height: 100vh;
  width: 100%;
`; */

/* interface DynamicBorderProps {
  theme: any;
} */

/* const DynamicBorder = styled(motion.div)<DynamicBorderProps>`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 2px;
  background-color: black;
  ${({ theme }) => theme.breakpoints.down("lg")} {
    display: none;
  }
`; */

/* interface StyledMotionDivProps {
  theme: any;
} */

/* const StyledMotionDiv = styled(motion.div)<StyledMotionDivProps>`
  ${({ theme }) => theme.breakpoints.down("lg")} {
    &.styled-motion-div--1 {
      display: none;
    }
  }
`;
 */
